import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  InitiateConversion,
  TradingAccountOption,
} from '@pages/Traders-Room/Balances/TradingAccounts/TradingAccountBalances'

import { useModal } from '@context/ModalsContext'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Body2, Heading4 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { DummyIcon } from '@ui/icons/Dummy.icon'
import { ExchangeIcon } from '@ui/icons/Exchange.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { NameDto } from '@model/NameDto'
import { CurrencyBalance, TradingAccount } from '@model/TradingAccount'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { SupportModal } from '@shared/modals/Support.modal'

import styles from './TradingAccountBalanceCard.module.scss'

interface TradingAccountBalanceCardProps {
  balance: CurrencyBalance
  restrictions?: NameDto[]
  handleSetSelectedTradingAccount(): void
  initiateConversion: ({ currency, balance }: InitiateConversion) => void
  setTAOptionsData: (tradingAccountOption?: TradingAccountOption) => void
  TAOptionsData?: TradingAccountOption
  initiateTransfer: () => void
  hasTransferOutRestriction: boolean
}

const getBalanceByCurrency = (account: TradingAccount | undefined, currency: CurrencyType) =>
  account?.platformOverview?.balances?.find((b) => b.currency === currency)

export const TradingAccountBalanceCard: React.FC<TradingAccountBalanceCardProps> = ({
  balance,
  handleSetSelectedTradingAccount,
  initiateTransfer,
  hasTransferOutRestriction,
  initiateConversion,
  TAOptionsData,
  setTAOptionsData,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const restrictionModal = useModal(SupportModal, {
    title: t('Temporarily unavailable'),
    description: t('Please contact Support for assistance.'),
  })

  return (
    <div className={styles.card}>
      <div className={styles.titleWrapper}>
        <IconButton.Ghost
          icon={hasTransferOutRestriction ? WarningIcon : DummyIcon}
          onClick={hasTransferOutRestriction ? () => restrictionModal.open() : undefined}
        />
        <Body2> {balance.currency}</Body2>
        <Popover
          isOpen={!!TAOptionsData?.tradingAccount && TAOptionsData?.currency === balance.currency}
          setIsOpen={(open) =>
            open ? handleSetSelectedTradingAccount() : setTAOptionsData(undefined)
          }
          content={
            <PopoverCard>
              <PopoverList>
                <ListItem
                  startSlot={{ icon: ExchangeIcon }}
                  disabled={hasTransferOutRestriction}
                  size='lg'
                  onClick={() => {
                    const selectedBalance = getBalanceByCurrency(
                      TAOptionsData?.tradingAccount,
                      balance.currency
                    )
                    initiateConversion({
                      currency: balance.currency,
                      balance: selectedBalance?.balance,
                      isTransferDisabled: hasTransferOutRestriction,
                    })
                  }}
                >
                  {t('Wallet.Convert')}
                </ListItem>
              </PopoverList>
            </PopoverCard>
          }
        >
          <IconButton.Ghost icon={KebabIcon} onClick={handleSetSelectedTradingAccount} />
        </Popover>
      </div>
      <Heading4 className='mb-4 mt-4 has-text-weight-semibold'>
        {formatMoney(balance.balance, balance.currency)}
      </Heading4>
      <div className='mb-4'>
        <AlertIconWrapper fullWidth spaced isActive={hasTransferOutRestriction}>
          <Tooltip
            fullWidth
            content={t('This action is currently unavailable.')}
            disabled={!hasTransferOutRestriction}
          >
            <Button.Secondary
              size='md'
              fullWidth
              disabled={hasTransferOutRestriction}
              onClick={initiateTransfer}
            >
              {t('Transfer')}
            </Button.Secondary>
          </Tooltip>
        </AlertIconWrapper>
      </div>
    </div>
  )
}
